import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { testsApi } from '@/api/tests';
import DynamicSurveyForm from '@/components/form/DynamicSurveyForm';
import TestApplicationInstructions from '@/components/tests/TestApplicationInstructions';
import TestPageLayout from '@/components/tests/TestPageLayout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import useScrollProgress from '@/hooks/use-scroll-progress';
import type { Survey } from '@/api/surveys';

export default function TestPreview() {
    const { id } = useParams();
    const navigate = useNavigate();
    const scrollProgress = useScrollProgress();

    const { data: test, isLoading } = useQuery({
        queryKey: ['tests', id],
        queryFn: () => testsApi.getById(id || ''),
        enabled: Boolean(id),
        retry: false,
    });

    const testDefinition = test as unknown as Survey;

    if (isLoading) return <Loading />;

    if (!test) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Instrumento não encontrado
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        O instrumento que você está tentando visualizar não existe.
                    </p>
                    <Button onClick={() => navigate('/assets')}>Voltar para Instrumentos</Button>
                </div>
            </div>
        );
    }

    if (!testDefinition.sections || testDefinition.sections.length === 0) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
                <div className="text-center">
                    <h1 className="mb-2 text-2xl font-bold text-foreground">
                        Estrutura do instrumento indisponível
                    </h1>
                    <p className="mb-6 text-muted-foreground">
                        Este instrumento ainda não possui perguntas configuradas.
                    </p>
                    <Button onClick={() => navigate('/assets')}>Voltar para Instrumentos</Button>
                </div>
            </div>
        );
    }

    return (
        <TestPageLayout progress={scrollProgress} onBack={() => navigate('/assets')}>
            <div className="mb-8 rounded-xl bg-card p-6 shadow-elevation-1 md:p-8">
                <h1 className="mb-6 border-b pb-4 text-2xl font-bold text-foreground">
                    {testDefinition.name}
                </h1>

                <TestApplicationInstructions
                    instructions={test.details?.application_instructions}
                    className="mb-6"
                />

                {test.details?.attribution && (
                    <p className="mb-6 text-xs text-muted-foreground">{test.details.attribution}</p>
                )}

                <Alert variant="info">
                    <strong>Modo de Visualização:</strong> Este é um exemplo de como o paciente verá
                    o instrumento. Os campos estão desabilitados e o botão de envio não funciona.
                </Alert>
            </div>

            <DynamicSurveyForm
                survey={testDefinition}
                onSubmit={() => {}}
                disabled={true}
                variant="flat"
            >
                <div className="mt-8 flex justify-end">
                    <Button
                        type="submit"
                        size="lg"
                        className="w-full cursor-not-allowed opacity-50 md:w-auto"
                        disabled={true}
                    >
                        Enviar Respostas
                    </Button>
                </div>
            </DynamicSurveyForm>
        </TestPageLayout>
    );
}
